import type { SavedViewPickerTexts } from "../savedViews/pickerTexts";
import { SavedViewPicker } from "../savedViews/SavedViewPicker";
import { parseContextViewPayload } from "./contextViewPayload";
import { contextPickerSlot } from "./ContextViewSelector.css";
import { describeContextView } from "./describeContextView";
import type { ContextViewState } from "./useContextViewState";

const SAVE_TOGGLES = [
  { key: "isDefault", label: "Set as the default context" }
] as const;

/**
 * The context list is one list for every cube page, so its default is one
 * default: the wording says "default", not "this page".
 */
const TEXTS: SavedViewPickerTexts = {
  newLabel: "New Context",
  saveTitle: "Save current context",
  namePlaceholder: "Context name",
  edit: {
    title: "Edit context",
    namePlaceholder: "Context name",
    refreshLabel: "Replace the saved context with the current selection"
  },
  delete: {
    title: "Delete context",
    message: (name) => `Delete "${name}"? Your selection stays as it is.`
  },
  setDefault: {
    current: "This is the default context",
    promote: "Set as the default context"
  }
};

interface ContextViewSelectorProps {
  /** The page's Context layer (``useContextViewState``). */
  views: ContextViewState;
}

/**
 * The control that names the context selection: the user's saved contexts
 * in one list, shared by every cube page. A context view has one section,
 * so the save dialog asks only for a name and whether it opens the pages.
 */
export function ContextViewSelector({ views }: ContextViewSelectorProps) {
  return (
    <SavedViewPicker
      label="Context"
      slotClassName={contextPickerSlot}
      texts={TEXTS}
      views={views.views}
      describe={(view) => {
        const payload = parseContextViewPayload(view.control_state);
        return payload ? describeContextView(payload) : "Unreadable";
      }}
      activeView={views.activeView}
      activeSavedView={views.activeSavedView}
      isDirty={views.isDirty}
      onPickSaved={views.applySavedView}
      writes={views}
      saveToggles={SAVE_TOGGLES}
      saveDefaults={{ isDefault: false }}
      onSave={(name, { isDefault }) => views.saveCurrentView(name, isDefault)}
    />
  );
}
